"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import navIndex from "@/data/nav/categoryIndex.json";
import { imageForSlug } from "@/lib/categoryImages";
import { getCategoryUrl, getSubcategoryUrl } from "@/lib/categoryUrls";

/**
 * Hand-curated iconic showcase subcategories for each master realm.
 * Guarantees that the homepage cards highlight the most popular, diverse,
 * and visually stunning generators rather than raw alphabetical order.
 */
const CURATED_SHOWCASE: Record<
  string,
  { slug: string; displayName?: string }[]
> = {
  "pop-culture": [
    { slug: "dnd-names", displayName: "Dungeons & Dragons" },
    { slug: "sw-mandalorian-names", displayName: "Star Wars: Mandalorian" },
    { slug: "hp-wizard-names", displayName: "Harry Potter: Wizards" },
    { slug: "the-witcher-elf-names", displayName: "The Witcher" },
    { slug: "cyberpunk-names", displayName: "Cyberpunk" },
  ],
  "fantasy-names": [
    { slug: "dragon-names", displayName: "Dragon Names" },
    { slug: "dark-elf-names", displayName: "Dark Elf Names" },
    { slug: "demon-names", displayName: "Demon Names" },
    { slug: "angel-names", displayName: "Angel Names" },
    { slug: "barbarian-names", displayName: "Barbarian Names" },
  ],
  "real-names": [
    { slug: "japanese-names", displayName: "Japanese Names" },
    { slug: "viking-names", displayName: "Viking & Norse" },
    { slug: "celtic-names", displayName: "Celtic Names" },
    { slug: "ancient-greek-names", displayName: "Ancient Greek" },
    { slug: "arabic-muslim-names", displayName: "Arabic / Muslim" },
  ],
  "places-locations": [
    { slug: "castle-names", displayName: "Castles & Fortresses" },
    { slug: "tavern-names", displayName: "Taverns & Inns" },
    { slug: "city-town-names", displayName: "Cities & Towns" },
    { slug: "island-names", displayName: "Islands & Coastlines" },
    { slug: "dungeon-names", displayName: "Dungeons & Crypts" },
  ],
  "other-names": [
    { slug: "ship-names", displayName: "Ships & Galleons" },
    { slug: "spell-names", displayName: "Spells & Incantations" },
    { slug: "sword-names", displayName: "Legendary Swords" },
    { slug: "alliance-names", displayName: "Alliances & Guilds" },
    { slug: "artifact-names", displayName: "Ancient Artifacts" },
  ],
  "description-generators": [
    { slug: "dragon-descriptions", displayName: "Dragon Descriptions" },
    { slug: "castle-descriptions", displayName: "Castle Architecture" },
    { slug: "dungeon-descriptions", displayName: "Dungeon Environments" },
    { slug: "tavern-descriptions", displayName: "Tavern Atmospheres" },
    { slug: "plate-armor-descriptions", displayName: "Plate Armor Lore" },
  ],
};

function getNormalizedKey(name: string): string {
  return name.toLowerCase().replace(/names?|species|characters?|\W/g, "");
}

interface SubcategoryItem {
  name: string;
  slug: string;
  description: string;
  count?: number;
  icon?: string;
}

export default function FeatureSections() {
  return (
    <div className="space-y-24">
      {navIndex.categories.map((category) => {
        const allSubs: SubcategoryItem[] = (category.subcategories || []) as SubcategoryItem[];
        const curatedConfigs = CURATED_SHOWCASE[category.slug] || [];

        // Build top 5 showcase cards from curated configuration
        const top: SubcategoryItem[] = curatedConfigs
          .map((cfg) => {
            const found = allSubs.find((s) => s.slug === cfg.slug);
            if (!found) return null;
            return {
              ...found,
              name: cfg.displayName || found.name,
            };
          })
          .filter(Boolean) as SubcategoryItem[];

        // Fallback if curated items not found
        if (top.length === 0) {
          top.push(...allSubs.slice(0, 5));
        }

        const topSlugSet = new Set(top.map((t) => t.slug));
        const seenKeys = new Set(top.map((t) => getNormalizedKey(t.name)));

        // Select 12 diverse, non-duplicate secondary items for the explore grid
        const rest: SubcategoryItem[] = [];
        for (const sub of allSubs) {
          if (topSlugSet.has(sub.slug)) continue;
          const normKey = getNormalizedKey(sub.name);
          if (seenKeys.has(normKey)) continue;

          seenKeys.add(normKey);
          rest.push(sub);
          if (rest.length >= 12) break;
        }

        return (
          <section key={category.slug} className="relative">
            {/* Category Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{category.icon || "✨"}</span>
                  <Badge variant="outline" className="text-xs">
                    Curated Showcase
                  </Badge>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold font-fantasy text-foreground">
                  {category.name}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
                  {category.description}
                </p>
              </div>

              <Link
                href={getCategoryUrl(category.slug)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline group shrink-0"
              >
                <span>Explore all {allSubs.length} {category.name}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Visual Highlight Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-5 mb-6">
              {top.map((sub, i) => (
                <motion.div
                  key={sub.slug}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  whileHover={{ y: -4 }}
                >
                  <Link
                    href={getSubcategoryUrl(category.slug, sub.slug)}
                    className="group relative block overflow-hidden rounded-2xl border border-border/80 bg-card shadow-md hover:shadow-xl hover:border-primary/50 transition-all aspect-[4/5]"
                  >
                    <Image
                      src={imageForSlug(category.slug, sub.slug)}
                      alt={`${sub.name} in ${category.name.replace(" Names", "")}`}
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 20vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 w-full p-4">
                      <h3 className="text-sm font-bold text-white group-hover:text-primary transition-colors line-clamp-1">
                        {sub.name}
                      </h3>
                      <p className="text-[10px] text-zinc-300 uppercase tracking-wider mt-0.5">
                        {category.name.replace(" Names", "")}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Additional Subcategory Grid */}
            {rest.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {rest.map((sub) => (
                  <Link
                    key={sub.slug}
                    href={getSubcategoryUrl(category.slug, sub.slug)}
                    className="group rounded-xl border border-border/70 bg-card/60 p-3.5 hover:border-primary/50 hover:bg-accent hover:shadow-sm transition-all"
                  >
                    <h4 className="font-semibold text-xs text-foreground group-hover:text-primary line-clamp-1">
                      {sub.name}
                    </h4>
                    <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2 leading-tight">
                      {sub.description}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
